/**
 * Biome-tinted grass block sides.
 *
 * Vanilla draws a grayscale, biome-tinted overlay on top of the grass block's
 * side. We bake the same thing into the existing side tile instead of adding
 * a texture layer: every green pixel of the side tile's grass strip becomes
 * grayscale with a marker alpha, and the
 * voxel shader multiplies the biome tint into exactly those pixels
 * (see MASK_ALPHA in materials.ts). Dirt pixels keep alpha 1 and stay untinted.
 */

import { ATLAS_COLS, CELL } from '../atlasLayout';
import { T } from '../blocks';

/** Alpha written into tintable pixels (0 would make the canvas drop their RGB). */
export const MASK_ALPHA = 128;

function cellData(ctx: CanvasRenderingContext2D, tile: number): ImageData {
  return ctx.getImageData((tile % ATLAS_COLS) * CELL, Math.floor(tile / ATLAS_COLS) * CELL, CELL, CELL);
}

function luma(d: Uint8ClampedArray, i: number): number {
  return d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
}

/** Rewrite a side tile's grass strip in place. Call once, before the atlas is uploaded. */
function bakeSideMask(atlas: HTMLCanvasElement, tile: number): void {
  const ctx = atlas.getContext('2d', { willReadFrequently: true })!;
  const side = cellData(ctx, tile);
  const top = cellData(ctx, T.GRASS_TOP);
  const s = side.data;
  const n = CELL * CELL;

  // Grass strip = green pixels. The side tile also carries dark brown fringe
  // shadow pixels under the grass; those must stay untinted dirt, or the
  // tint turns them into a black edge.
  const isGrass = new Uint8Array(n);
  let count = 0;
  for (let p = 0, i = 0; p < n; p++, i += 4) {
    if (s[i + 1] > s[i] + 6 && s[i + 1] > s[i + 2]) {
      isGrass[p] = 1;
      count++;
    }
  }
  if (count === 0) return;

  // Match the strip's brightness to the (grayscale) top tile so side and top
  // read as the same grass once tinted.
  let stripLuma = 0, topLuma = 0;
  for (let p = 0, i = 0; p < n; p++, i += 4) {
    if (isGrass[p]) stripLuma += luma(s, i);
    topLuma += luma(top.data, i);
  }
  const gain = (topLuma / n) / Math.max(1, stripLuma / count);

  for (let p = 0, i = 0; p < n; p++, i += 4) {
    if (!isGrass[p]) continue;
    const g = Math.min(255, Math.round(luma(s, i) * gain));
    s[i] = s[i + 1] = s[i + 2] = g;
    s[i + 3] = MASK_ALPHA;
  }
  ctx.putImageData(side, (tile % ATLAS_COLS) * CELL, Math.floor(tile / ATLAS_COLS) * CELL);
}

/** Bake the grass-strip tint mask into every side tile that carries one: the
 *  grass block side. The dirt path side used to carry a composited copy of
 *  that strip; it now shows its own tan top row (see MC_TILE_BASES), so it
 *  must not be masked — vanilla paths are untinted. Call once, before the
 *  atlas is uploaded. */
export function bakeGrassSideMask(atlas: HTMLCanvasElement): void {
  bakeSideMask(atlas, T.GRASS_SIDE);
}
